import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiAlertCircle,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEye,
  FiFlag,
  FiList,
  FiMessageSquare,
  FiPlus,
  FiRefreshCw,
  FiUser,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import "./RequirementTasksDrawer.css";

const FINAL_STATUSES = [
  "COMPLETED",
  "CLOSED",
  "CANCELLED",
];

const formatLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1)
    )
    .join(" ");

const formatDate = (
  value,
  withTime = false
) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    withTime
      ? {
          dateStyle: "medium",
          timeStyle: "short",
        }
      : {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
  ).format(date);
};

const getCustomerName = (
  requirement
) =>
  String(
    requirement?.customer
      ?.customerName ||
      requirement?.customer?.name ||
      requirement?.customerName ||
      "Customer"
  ).trim();

const getCustomerMobile = (
  requirement
) =>
  String(
    requirement?.customer?.mobile ||
      requirement?.customer?.phone ||
      requirement?.mobile ||
      ""
  ).trim();

const getRequirementCode = (
  requirement
) =>
  String(
    requirement?.requirementCode ||
      requirement?.code ||
      requirement?._id ||
      "-"
  ).trim();

const getPriorityClass = (
  priority
) => {
  const normalized =
    String(
      priority || ""
    ).toLowerCase();

  return `requirement-task-priority requirement-task-priority-${normalized}`;
};

const getStatusClass = (
  status
) => {
  const normalized = String(
    status || ""
  )
    .trim()
    .toLowerCase()
    .replace(
      /_/g,
      "-"
    );

  return `requirement-task-status requirement-task-status-${normalized}`;
};

const buildTaskComputedData = (
  task
) => {
  const dueDate = task?.dueDate
    ? new Date(task.dueDate)
    : null;

  const followUpAt =
    task?.followUpAt
      ? new Date(
          task.followUpAt
        )
      : null;

  const now = new Date();

  const isFinal =
    FINAL_STATUSES.includes(
      String(
        task?.status || ""
      ).toUpperCase()
    );

  const todayStart =
    new Date();

  todayStart.setHours(
    0,
    0,
    0,
    0
  );

  const todayEnd =
    new Date();

  todayEnd.setHours(
    23,
    59,
    59,
    999
  );

  return {
    isOverdue:
      Boolean(
        dueDate &&
          dueDate < now &&
          !isFinal
      ),

    isDueToday:
      Boolean(
        dueDate &&
          dueDate >= todayStart &&
          dueDate <= todayEnd &&
          !isFinal
      ),

    isFollowUpDue:
      Boolean(
        followUpAt &&
          followUpAt <= now &&
          !isFinal
      ),
  };
};

const getTaskRowClass = (
  task
) => {
  const computed =
    buildTaskComputedData(
      task
    );

  if (
    task?.hasAdminUnreadActivity
  ) {
    return "requirement-task-row requirement-task-row-unread";
  }

  if (computed.isOverdue) {
    return "requirement-task-row requirement-task-row-overdue";
  }

  if (
    String(
      task?.priority || ""
    ).toUpperCase() ===
    "URGENT"
  ) {
    return "requirement-task-row requirement-task-row-urgent";
  }

  if (
    computed.isFollowUpDue
  ) {
    return "requirement-task-row requirement-task-row-follow-up";
  }

  if (
    computed.isDueToday
  ) {
    return "requirement-task-row requirement-task-row-due-today";
  }

  return "requirement-task-row";
};

const RequirementTasksDrawer = ({
  open,
  requirement,
  onClose,
  onAssignTask,
  onViewTask,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();

  const [tasks, setTasks] =
    useState([]);

  const [summary, setSummary] =
    useState({
      totalTasks: 0,
      openTasks: 0,
      completedTasks: 0,
      cancelledTasks: 0,
      unreadTaskCount: 0,
      hasUnreadTaskUpdate:
        false,
    });

  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
    });

  const [status, setStatus] =
    useState("");

  const [priority, setPriority] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const fetchTasks = useCallback(
    async () => {
      if (
        !open ||
        !requirement?._id
      ) {
        return;
      }

      try {
        setLoading(true);
        setError("");

        const query =
          new URLSearchParams({
            page: String(
              pagination.page
            ),

            limit: String(
              pagination.limit
            ),

            status,

            priority,
          });

        const response =
          await api.get(
            `${config.getRequirementTasks(
              requirement._id
            )}?${query.toString()}`
          );

        if (
          response?.status ===
          "success"
        ) {
          setTasks(
            Array.isArray(
              response?.data
            )
              ? response.data
              : []
          );

          setSummary({
            totalTasks: Number(
              response?.summary
                ?.totalTasks ||
                0
            ),

            openTasks: Number(
              response?.summary
                ?.openTasks ||
                0
            ),

            completedTasks:
              Number(
                response?.summary
                  ?.completedTasks ||
                  0
              ),

            cancelledTasks:
              Number(
                response?.summary
                  ?.cancelledTasks ||
                  0
              ),

            unreadTaskCount:
              Number(
                response?.summary
                  ?.unreadTaskCount ||
                  0
              ),

            hasUnreadTaskUpdate:
              Boolean(
                response?.summary
                  ?.hasUnreadTaskUpdate
              ),
          });

          setPagination(
            (previous) => ({
              ...previous,

              page: Number(
                response?.pagination
                  ?.page ||
                  previous.page
              ),

              limit: Number(
                response?.pagination
                  ?.limit ||
                  previous.limit
              ),

              total: Number(
                response?.pagination
                  ?.totalRecords ??
                  response?.pagination
                    ?.total ??
                  0
              ),

              totalPages:
                Math.max(
                  Number(
                    response
                      ?.pagination
                      ?.totalPages ||
                      1
                  ),
                  1
                ),
            })
          );
        } else {
          setTasks([]);

          setError(
            response?.message ||
              "Unable to fetch requirement tasks."
          );
        }
      } catch (requestError) {
        console.error(
          "Get requirement tasks error:",
          requestError
        );

        setTasks([]);

        setError(
          requestError?.response
            ?.data?.message ||
            requestError?.message ||
            "Unable to fetch requirement tasks."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      open,
      requirement?._id,
      pagination.page,
      pagination.limit,
      status,
      priority,
    ]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setTasks([]);

    setSummary({
      totalTasks: Number(
        requirement
          ?.taskSummary
          ?.totalTasks || 0
      ),

      openTasks: Number(
        requirement
          ?.taskSummary
          ?.openTasks || 0
      ),

      completedTasks:
        Number(
          requirement
            ?.taskSummary
            ?.completedTasks ||
            0
        ),

      cancelledTasks:
        Number(
          requirement
            ?.taskSummary
            ?.cancelledTasks ||
            0
        ),

      unreadTaskCount:
        Number(
          requirement
            ?.taskSummary
            ?.unreadTaskCount ||
            0
        ),

      hasUnreadTaskUpdate:
        Boolean(
          requirement
            ?.taskSummary
            ?.hasUnreadTaskUpdate
        ),
    });

    setPagination({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
    });

    setStatus("");
    setPriority("");
    setError("");
  }, [
    open,
    requirement,
  ]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (
      event
    ) => {
      if (
        event.key === "Escape"
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [open, onClose]);

  const handlePageChange = (
    nextPage
  ) => {
    if (
      nextPage < 1 ||
      nextPage >
        pagination.totalPages ||
      loading
    ) {
      return;
    }

    setPagination(
      (previous) => ({
        ...previous,
        page: nextPage,
      })
    );
  };

  const handleLimitChange = (
    event
  ) => {
    setPagination(
      (previous) => ({
        ...previous,
        page: 1,
        limit: Number(
          event.target.value
        ),
      })
    );
  };

  const handleStatusChange = (
    event
  ) => {
    setStatus(
      event.target.value
    );

    setPagination(
      (previous) => ({
        ...previous,
        page: 1,
      })
    );
  };

  const handlePriorityChange = (
    event
  ) => {
    setPriority(
      event.target.value
    );

    setPagination(
      (previous) => ({
        ...previous,
        page: 1,
      })
    );
  };

  const handleRefresh =
    async () => {
      await fetchTasks();
    };

  const handleTaskView = async (
    task
  ) => {
    try {
      if (
        task
          ?.hasAdminUnreadActivity &&
        config.markTaskRead
      ) {
        await api.patch(
          config.markTaskRead(
            task._id
          ),
          {}
        );

        setTasks(
          (previous) =>
            previous.map(
              (item) =>
                String(
                  item._id
                ) ===
                String(task._id)
                  ? {
                      ...item,
                      hasAdminUnreadActivity:
                        false,
                      adminUnreadCount:
                        0,
                    }
                  : item
            )
        );
      }
    } catch (requestError) {
      console.error(
        "Mark task read error:",
        requestError
      );
    }

    if (
      typeof onViewTask ===
      "function"
    ) {
      onViewTask(task);

      return;
    }

    swal.info({
      icon: "info",
      title:
        task?.taskCode ||
        "Task Details",
      text:
        "Task details drawer will be connected in the Task Management module.",
    });
  };

  if (
    !open ||
    !requirement
  ) {
    return null;
  }

  const customerName =
    getCustomerName(
      requirement
    );

  const customerMobile =
    getCustomerMobile(
      requirement
    );

  const requirementCode =
    getRequirementCode(
      requirement
    );

  return ReactDOM.createPortal(
    <div className="requirement-tasks-root">
      <button
        type="button"
        className="requirement-tasks-backdrop"
        onClick={onClose}
        aria-label="Close requirement tasks"
      />

      <aside
        className="requirement-tasks-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="requirement-tasks-title"
      >
        <header className="requirement-tasks-header">
          <div className="requirement-tasks-header-content">
            <span className="requirement-tasks-header-icon">
              <FiList />
            </span>

            <div>
              <small>
                CUSTOMER REQUIREMENT
              </small>

              <h2 id="requirement-tasks-title">
                Linked Tasks
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="requirement-tasks-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </header>

        <section className="requirement-tasks-source">
          <div className="requirement-tasks-source-top">
            <div>
              <span>
                {
                  requirementCode
                }
              </span>

              <h3>
                {requirement
                  ?.requirementType ||
                  "Customer Requirement"}
              </h3>

              <p>
                {requirement
                  ?.description ||
                  "No requirement description available."}
              </p>
            </div>

            <button
              type="button"
              className="requirement-tasks-assign-button"
              onClick={() =>
                onAssignTask?.(
                  requirement
                )
              }
            >
              <FiPlus />
              Assign Task
            </button>
          </div>

          <div className="requirement-tasks-customer">
            <span className="requirement-tasks-customer-icon">
              <FiUser />
            </span>

            <div>
              <strong>
                {customerName}
              </strong>

              <span>
                {customerMobile ||
                  "Mobile not available"}
              </span>
            </div>
          </div>
        </section>

        <section className="requirement-tasks-summary">
          <article>
            <span>
              Total Tasks
            </span>

            <strong>
              {summary.totalTasks}
            </strong>

            <FiBriefcase />
          </article>

          <article>
            <span>
              Open Tasks
            </span>

            <strong>
              {summary.openTasks}
            </strong>

            <FiClock />
          </article>

          <article>
            <span>
              Completed
            </span>

            <strong>
              {
                summary.completedTasks
              }
            </strong>

            <FiCheckCircle />
          </article>

          <article
            className={
              summary.unreadTaskCount >
              0
                ? "has-unread"
                : ""
            }
          >
            <span>
              New Updates
            </span>

            <strong>
              {
                summary.unreadTaskCount
              }
            </strong>

            <FiMessageSquare />
          </article>
        </section>

        <section className="requirement-tasks-toolbar">
          <div className="requirement-tasks-filter">
            <label htmlFor="requirement-task-status-filter">
              Status
            </label>

            <select
              id="requirement-task-status-filter"
              value={status}
              onChange={
                handleStatusChange
              }
            >
              <option value="">
                All Statuses
              </option>

              <option value="PENDING">
                Pending
              </option>

              <option value="IN_PROGRESS">
                In Progress
              </option>

              <option value="WAITING_FOLLOW_UP">
                Waiting Follow-up
              </option>

              <option value="ON_HOLD">
                On Hold
              </option>

              <option value="COMPLETED">
                Completed
              </option>

              <option value="CLOSED">
                Closed
              </option>

              <option value="CANCELLED">
                Cancelled
              </option>
            </select>
          </div>

          <div className="requirement-tasks-filter">
            <label htmlFor="requirement-task-priority-filter">
              Priority
            </label>

            <select
              id="requirement-task-priority-filter"
              value={priority}
              onChange={
                handlePriorityChange
              }
            >
              <option value="">
                All Priorities
              </option>

              <option value="LOW">
                Low
              </option>

              <option value="MEDIUM">
                Medium
              </option>

              <option value="HIGH">
                High
              </option>

              <option value="URGENT">
                Urgent
              </option>
            </select>
          </div>

          <button
            type="button"
            className="requirement-tasks-refresh"
            onClick={
              handleRefresh
            }
            disabled={loading}
            title="Refresh tasks"
          >
            <FiRefreshCw
              className={
                loading
                  ? "spinning"
                  : ""
              }
            />
          </button>
        </section>

        <div className="requirement-tasks-body">
          {error ? (
            <div className="requirement-tasks-error">
              <FiAlertCircle />

              <div>
                <strong>
                  Unable to load tasks
                </strong>

                <span>
                  {error}
                </span>

                <button
                  type="button"
                  onClick={
                    handleRefresh
                  }
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : null}

          {loading &&
          tasks.length === 0 ? (
            <div className="requirement-tasks-loading">
              <div className="requirement-tasks-spinner" />

              <p>
                Loading linked tasks...
              </p>
            </div>
          ) : !loading &&
            tasks.length === 0 ? (
            <div className="requirement-tasks-empty">
              <FiBriefcase />

              <h3>
                No linked tasks found
              </h3>

              <p>
                Assign this customer requirement to an employee for follow-up and execution.
              </p>

              <button
                type="button"
                onClick={() =>
                  onAssignTask?.(
                    requirement
                  )
                }
              >
                <FiPlus />
                Assign First Task
              </button>
            </div>
          ) : (
            <div className="requirement-task-list">
              {tasks.map((task) => {
                const computed =
                  buildTaskComputedData(
                    task
                  );

                return (
                  <article
                    key={task._id}
                    className={getTaskRowClass(
                      task
                    )}
                  >
                    <div className="requirement-task-card-top">
                      <div className="requirement-task-code-group">
                        <span>
                          {task.taskCode ||
                            "TASK"}
                        </span>

                        {task
                          .hasAdminUnreadActivity ? (
                          <strong className="requirement-task-new-badge">
                            New Update
                          </strong>
                        ) : null}
                      </div>

                      <div className="requirement-task-badges">
                        <span
                          className={getPriorityClass(
                            task.priority
                          )}
                        >
                          <FiFlag />

                          {formatLabel(
                            task.priority
                          )}
                        </span>

                        <span
                          className={getStatusClass(
                            task.status
                          )}
                        >
                          {formatLabel(
                            task.status
                          )}
                        </span>
                      </div>
                    </div>

                    <h4>
                      {task.title ||
                        "Untitled Task"}
                    </h4>

                    <p className="requirement-task-description">
                      {task.description ||
                        "No task description available."}
                    </p>

                    <div className="requirement-task-employee">
                      <span className="requirement-task-avatar">
                        {task?.assignedTo
                          ?.profileImage ? (
                          <img
                            src={
                              task
                                .assignedTo
                                .profileImage
                            }
                            alt={
                              task
                                .assignedTo
                                .name
                            }
                          />
                        ) : (
                          String(
                            task?.assignedTo
                              ?.name ||
                              "E"
                          )
                            .charAt(0)
                            .toUpperCase()
                        )}
                      </span>

                      <div>
                        <small>
                          Assigned To
                        </small>

                        <strong>
                          {task?.assignedTo
                            ?.name ||
                            "Employee not available"}
                        </strong>

                        <span>
                          {[
                            task?.assignedTo
                              ?.employeeCode,
                            task?.assignedTo
                              ?.department,
                            task?.assignedTo
                              ?.designation,
                          ]
                            .filter(
                              Boolean
                            )
                            .join(
                              " • "
                            )}
                        </span>
                      </div>
                    </div>

                    <div className="requirement-task-meta-grid">
                      <div
                        className={
                          computed.isOverdue
                            ? "danger"
                            : computed.isDueToday
                              ? "warning"
                              : ""
                        }
                      >
                        <FiCalendar />

                        <div>
                          <small>
                            Due Date
                          </small>

                          <strong>
                            {formatDate(
                              task.dueDate
                            )}
                          </strong>

                          {computed.isOverdue ? (
                            <span>
                              Overdue
                            </span>
                          ) : computed.isDueToday ? (
                            <span>
                              Due today
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div
                        className={
                          computed.isFollowUpDue
                            ? "warning"
                            : ""
                        }
                      >
                        <FiClock />

                        <div>
                          <small>
                            Follow-up
                          </small>

                          <strong>
                            {formatDate(
                              task.followUpAt,
                              true
                            )}
                          </strong>

                          {computed.isFollowUpDue ? (
                            <span>
                              Follow-up due
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="requirement-task-progress-row">
                      <div>
                        <span>
                          Progress
                        </span>

                        <strong>
                          {Number(
                            task.progress ||
                              0
                          )}
                          %
                        </strong>
                      </div>

                      <div className="requirement-task-progress-bar">
                        <span
                          style={{
                            width: `${Math.min(
                              Math.max(
                                Number(
                                  task.progress ||
                                    0
                                ),
                                0
                              ),
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>

                    {task.latestActivityMessage ? (
                      <div className="requirement-task-latest-activity">
                        <FiMessageSquare />

                        <div>
                          <small>
                            Latest Activity
                          </small>

                          <p>
                            {
                              task.latestActivityMessage
                            }
                          </p>

                          <span>
                            {[
                              task.latestActivityByName,
                              formatDate(
                                task.latestActivityAt,
                                true
                              ),
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " • "
                              )}
                          </span>
                        </div>
                      </div>
                    ) : null}

                    <footer className="requirement-task-card-footer">
                      <span>
                        Created{" "}
                        {formatDate(
                          task.createdAt
                        )}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          handleTaskView(
                            task
                          )
                        }
                      >
                        <FiEye />
                        View Task
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <footer className="requirement-tasks-footer">
          <div className="requirement-tasks-pagination-info">
            Showing{" "}
            <strong>
              {tasks.length}
            </strong>{" "}
            of{" "}
            <strong>
              {pagination.total}
            </strong>{" "}
            tasks
          </div>

          <div className="requirement-tasks-pagination">
            <select
              value={
                pagination.limit
              }
              onChange={
                handleLimitChange
              }
            >
              <option value={5}>
                5 / page
              </option>

              <option value={10}>
                10 / page
              </option>

              <option value={20}>
                20 / page
              </option>
            </select>

            <button
              type="button"
              onClick={() =>
                handlePageChange(
                  pagination.page -
                    1
                )
              }
              disabled={
                pagination.page <=
                  1 ||
                loading
              }
            >
              <FiChevronLeft />
            </button>

            <span>
              Page{" "}
              <strong>
                {pagination.page}
              </strong>{" "}
              of{" "}
              <strong>
                {
                  pagination.totalPages
                }
              </strong>
            </span>

            <button
              type="button"
              onClick={() =>
                handlePageChange(
                  pagination.page +
                    1
                )
              }
              disabled={
                pagination.page >=
                  pagination.totalPages ||
                loading
              }
            >
              <FiChevronRight />
            </button>
          </div>
        </footer>
      </aside>
    </div>,
    document.body
  );
};

export default RequirementTasksDrawer;